import React, { useEffect, useState } from "react";
import { getApp } from "firebase/app";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import './Travel.css';

function Travel() {
  const auth = getAuth(getApp());
  // default to true to avoid briefly showing Halifax Minster
  // to users who are only invited to the night do:
  const [isNightDo, setIsNightDo] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setIsNightDo(true);
        return;
      }

      user.getIdTokenResult()
        .then((idTokenResult) => {
          const isNightDo = idTokenResult.claims?.isNightDo ?? true;
          setIsNightDo(isNightDo);
        })
        .catch((err) => console.error("getIdTokenResult error:", err));
    });

    return () => unsubscribe();
  }, [auth]);

  return (
    <div className="container">
      <h1 className="tangerine-bold">Travel &amp; Accommodation</h1>
      <p className="josefin-slab">
        For those travelling from outside of town, here is some information to help you get where you need to be.
      </p>

      <div className="content">
        <h2 className="josefin-slab upper">{isNightDo ? "Our Venue" : "Our Venues"}</h2>

        <div className="travel">
          {!isNightDo && (
            <div className="halifax-minster">
              <p className="josefin-slab" style={{ textAlign: 'center' }}><b>The Ceremony</b></p>
              <div className="mapouter">
                <div className="gmap_canvas">
                  <iframe
                    className="gmap_iframe"
                    width="100%"
                    frameBorder="0"
                    scrolling="no"
                    marginHeight="0"
                    marginWidth="0"
                    title="Map showing Halifax Minster, ceremony location"
                    loading="lazy"
                    src="https://maps.google.com/maps?width=600&amp;height=250&amp;hl=en&amp;q=Halifax Minster&amp;t=p&amp;z=10&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
                  ></iframe>
                </div>
              </div>
            </div>
          )}

          {!isNightDo && <div className="divider" ></div>}

          <div className="manor-house" style={isNightDo ? { gridColumn: '1 / -1' } : {}}>
            <p className="josefin-slab" style={{ textAlign: 'center' }}><b>The Reception</b></p>
            <div className="mapouter">
              <div className="gmap_canvas">
                <iframe
                  className="gmap_iframe"
                  width="100%"
                  frameBorder="0"
                  scrolling="no"
                  marginHeight="0"
                  marginWidth="0"
                  title="Map showing The Manor House, Lindley, reception location"
                  loading="lazy"
                  src="https://maps.google.com/maps?width=600&amp;height=250&amp;hl=en&amp;q=The Manor House, Lindley&amp;t=p&amp;z=10&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <h2 className="josefin-slab upper">Travel</h2>

        <div className="travel">
          <div className="train">
            <p className="josefin-slab"><b>Train from London</b></p>
            <p className="josefin-slab">
              If you're travelling the day before, Grand Central is the easiest and most cost-effective route, direct from London Kings Cross to Halifax.
              <br /><br />
              Alternatively, LNER run regular services from London to Leeds, where you can change for a local service to Halifax station - a 5 minute walk to the Minster.
              <br /><br />
              We recommend booking early to get the best prices!
            </p>
          </div>

          <div className="divider" ></div>

          <div className="air">
            <p className="josefin-slab"><b>International</b></p>
            <p className="josefin-slab">
              Manchester is the closest major international airport.
              <br /><br />
              From there you can take a direct train to Huddersfield - which is convenient for The Manor House and Briar Court - or change in central Manchester for a direct train to Halifax.
            </p>
          </div>
        </div>
      </div>

      <div className="content">
        <h2 className="josefin-slab upper">Accommodation</h2>
        <p className="josefin-slab">
          The Manor House only has a small number of rooms available - these are reserved for our wedding party of our very close family and friends.
          However, we have some accommodation options nearby for our guests if you are travelling from out of town:
        </p>

        <div className="travel">
          <div className="briar-court">
            <p className="josefin-slab"><b>Briar Court</b></p>
            <p className="josefin-slab">
              We have a block reservation at <a href="https://www.briarcourthotel.co.uk" target="_blank" rel="noopener noreferrer">Briar Court Hotel</a>, a 15 minute walk / 3 minute drive from The Manor House and 15 minute drive to Halifax Minster.
              <br /><br />
              To book, call <a href="tel:01484519902">01484 519902</a> and mention the reference <b>BCO37089</b> (Binns/Sellick wedding) to get the group rate of £109 per night for a double room including breakfast (£99 for single occupancy).
              <br /><br />
              You will not be charged at the time of booking, but a credit card is required to secure the reservation.
              Cancellations may be made before 2pm on 18<sup>th</sup> September, so we recommend booking even if you are unsure.
              <br /><br />
              Twin rooms and interconnecting rooms are also available on request, but are not included in our allocation so please book early to avoid disappointment.
              Check-in is from 2pm, and check-out before 11am. Free parking is available at this hotel.
            </p>
          </div>

          <div className="divider" ></div>

          <div className="plover-cottages">
            <p className="josefin-slab"><b>Plover Cottages</b></p>
            <p className="josefin-slab">
              For larger groups and families, Plover Cottages in Lindley is a group of nine luxury self-catered apartments just a 3 minute walk from The Manor House.
              <br /><br />
              These will likely sell out quickly so please visit <a href="https://www.plovercottages.co.uk" target="_blank" rel="noopener noreferrer">their website</a> to check availability and book early!
            </p>

            <br /><br />

            <p className="josefin-slab"><b>Cedar Court</b></p>
            <p className="josefin-slab">
              We have also been provided with a 10% discount code for Cedar Court Hotel, which is a 15 minute drive to Halifax Minster and a 5 minute drive from The Manor House.
              <br /><br />
              To book, please use <a href="https://reservations.travelclick.com/115216?RatePlanId=8969079&identifier=WED10OFF" target="_blank" rel="noopener noreferrer">this exclusive link</a>, or enter the code WED10OFF when booking direct on their website.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Travel;